import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarCheck,
  UtensilsCrossed,
  Sparkles,
  Car,
  MessageCircle,
  Clock,
} from 'lucide-react';
import { StoriesRow } from '@/features/stories/StoriesRow';
import { HeroCard } from '@/components/HeroCard';
import { NeonTile, type NeonGlow, type LucideIcon } from '@/components/NeonTile';
import { tilesByKey, useHomeTiles } from '@/features/home-tiles/useHomeTiles';

interface TileDef {
  key: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  glow: NeonGlow;
  to: string;
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: tilesData } = useHomeTiles();
  const tiles = tilesByKey(tilesData);

  const placeRow: TileDef[] = [
    {
      key: 'parking',
      title: t('home.parking'),
      subtitle: 'Как доехать',
      icon: Car,
      glow: 'emerald',
      to: '/page/parking',
    },
    {
      key: 'about',
      title: t('home.about'),
      subtitle: 'Концепция · арт',
      icon: Sparkles,
      glow: 'violet',
      to: '/page/about',
    },
  ];

  return (
    <div className="flex flex-col gap-7 pb-4 pt-3">
      <StoriesRow />

      <OpenStatus />

      {/* Hero duet — the two first-class actions */}
      <div className="grid grid-cols-2 gap-3">
        <HeroCard
          title="Забронировать"
          subtitle="Стол"
          variant={tiles.booking ? 'secondary' : 'primary'}
          icon={CalendarCheck}
          image={tiles.booking ?? undefined}
          cta="Перейти"
          track="click:home-booking"
          onClick={() => navigate('/booking')}
          style={{ animationDelay: '60ms' }}
        />
        <HeroCard
          title="Меню"
          subtitle="Еда · Бар · Кальян"
          variant={tiles.menu ? 'secondary' : 'primary'}
          icon={UtensilsCrossed}
          image={tiles.menu ?? undefined}
          cta="Открыть"
          track="click:home-menu"
          onClick={() => navigate('/menu')}
          style={{ animationDelay: '140ms' }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {placeRow.map((tile, i) => (
          <NeonTile
            key={tile.key}
            title={tile.title}
            subtitle={tile.subtitle}
            icon={tile.icon}
            glow={tile.glow}
            href={tile.to}
            image={tiles[tile.key as 'parking' | 'about']}
            delay={200 + i * 60}
            track={`click:home-${tile.key}`}
          />
        ))}
      </div>

      <NeonTile
        title={t('home.director')}
        subtitle="Есть что сказать"
        icon={MessageCircle}
        glow="cyan"
        href="/director"
        image={tiles.director}
        delay={320}
        wide
        track="click:home-director"
      />

      <Signature />
    </div>
  );
}

/** Ambient "we're open now" strip — editorial mono with a live ember dot. */
function OpenStatus() {
  const open = isOpenNow();
  const dotColor = open ? '#6ea987' : 'var(--ember)';
  const dotGlow = open
    ? '0 0 8px 1px rgba(110,169,135,0.6), 0 0 18px 4px rgba(110,169,135,0.22)'
    : '0 0 8px 1px rgba(194,58,46,0.55), 0 0 18px 4px rgba(194,58,46,0.2)';
  const ringColor = open ? 'rgba(110,169,135,0.32)' : 'rgba(194,58,46,0.32)';
  return (
    <div
      className="flex items-center gap-2.5 self-start rounded-full px-3.5 py-2 rise-in"
      style={{
        animationDelay: '30ms',
        border: `1px solid ${ringColor}`,
        background: 'rgba(255,255,255,0.025)',
        backdropFilter: 'blur(6px)',
        boxShadow: `0 0 24px -10px ${open ? 'rgba(110,169,135,0.45)' : 'rgba(194,58,46,0.45)'}`,
      }}
    >
      <span className="relative inline-flex w-2 h-2">
        <span
          className="absolute inset-0 rounded-full opacity-75 animate-ping"
          style={{ background: dotColor }}
        />
        <span
          className="relative rounded-full w-2 h-2"
          style={{ background: dotColor, boxShadow: dotGlow }}
        />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/85">
        {open ? 'Сейчас открыто' : 'Закрыто'}
      </span>
      <span className="h-3 w-px bg-white/10" aria-hidden />
      <Clock size={11} className="text-white/45" strokeWidth={1.6} />
      <span className="font-mono text-[10.5px] tracking-[0.14em] text-white/55">
        {scheduleHint()}
      </span>
    </div>
  );
}

function Signature() {
  return (
    <div className="mt-4 flex flex-col items-center gap-2 text-center">
      <div className="hairline w-16" />
      <div className="font-display text-[11px] uppercase tracking-[0.36em] text-white/45">
        JohnCalliano · Lounge
      </div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/32">
        Москва · ул. Солянка 1/2с1
      </div>
    </div>
  );
}

/** Current hour/day in Europe/Moscow, independent of the client device locale. */
function mskNow(): { h: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Moscow',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false,
  }).formatToParts(new Date());
  const byType = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const hour = parseInt(byType('hour'), 10);
  const minute = parseInt(byType('minute'), 10);
  const wd = byType('weekday'); // "Mon", "Tue", ...
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return { h: hour + minute / 60, day: map[wd] ?? 0 };
}

function isOpenNow(): boolean {
  const { h, day } = mskNow();
  // Fri (5) and Sat (6) sessions spill into Sat/Sun 00:00–04:00.
  const spilloverFromYesterday = (day === 6 || day === 0) && h < 4;
  if (spilloverFromYesterday) return true;
  // Every day opens at 12:00. Sun–Thu close at midnight (no further check);
  // Fri/Sat close at 04:00 next day — handled by the spillover check above.
  return h >= 12;
}

function scheduleHint(): string {
  const { day } = mskNow();
  return day === 5 || day === 6 ? '12:00 – 04:00' : '12:00 – 00:00';
}
